import { Box, Typography } from '@mui/material';
import { useState, type ReactElement } from 'react';

import type { AdminApi, AdminLegalDocumentType, AdminLegalVersionSummary } from '@/admin/adminApi';
import { formatCompactInstant } from '@/admin/data/payload';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { absoluteInstant } from '@/admin/operations/failedMessagePresentation';
import { AccessDenied, Badge, Button, Card, DataTable, EmptyState, ErrorState, LoadingState, type DataTableColumn } from '@/ui/kit';

import { actorLabel } from './legalDocument';

/**
 * Historique des versions d'un document : lecture seule (maquette P18).
 *
 * - **Le total est celui du serveur** : « 3 versions », jamais le nombre de lignes
 *   de la page. Les pages sont courtes (10) parce qu'un historique de document
 *   legal se lit de haut en bas, la plus recente d'abord.
 * - **Le brouillon ouvre la liste** s'il existe — il n'a pas de numero — mais n'a
 *   pas de bouton « Consulter » : il s'edite, a cote.
 * - **Consulter une version ne la modifie pas** : le bouton ouvre le texte en
 *   lecture seule (`onView`), aucune route n'ecrit une version publiee.
 * - La relecture est celle de l'ecran (`token`) : ni minuterie, ni appel de plus.
 */

const PER_PAGE = 10;

const versionLabel = (version: AdminLegalVersionSummary): string =>
  version.number === null ? 'Brouillon' : `Version ${String(version.number)}`;

const columns = (
  onView: (number: number) => void,
  viewing: number | null,
): readonly DataTableColumn<AdminLegalVersionSummary>[] => [
  {
    key: 'version',
    header: 'Version',
    primary: true,
    render: (version) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 700 }}>
          {versionLabel(version)}
        </Typography>
        {version.status === 'draft' ? <Badge label="Brouillon en cours" /> : null}
        {version.provisional ? <Badge label="Provisoire" tone="warning" /> : null}
      </Box>
    ),
  },
  {
    key: 'by',
    header: 'Par',
    render: (version) =>
      version.status === 'draft' ? `Écrit par ${actorLabel(version.author)}` : `Publiée par ${actorLabel(version.publishedBy)}`,
  },
  {
    key: 'date',
    header: 'Date',
    render: (version) => {
      const iso = version.publishedAt ?? version.savedAt;

      return (
        <time dateTime={iso} title={absoluteInstant(iso) ?? undefined} style={{ whiteSpace: 'nowrap' }}>
          {formatCompactInstant(iso)}
        </time>
      );
    },
  },
  {
    key: 'length',
    header: 'Taille',
    align: 'right',
    render: (version) => <span style={{ whiteSpace: 'nowrap' }}>{`${version.length.toLocaleString('fr-FR')} car.`}</span>,
  },
  {
    key: 'action',
    header: 'Action',
    align: 'right',
    render: (version) =>
      version.number === null ? null : (
        <Button
          size="S"
          aria-pressed={viewing === version.number}
          aria-label={`Consulter la version ${String(version.number)}`}
          onClick={() => {
            onView(version.number ?? 0);
          }}
        >
          Consulter
        </Button>
      ),
  },
];

export const VersionHistory = ({
  api,
  type,
  token,
  viewing,
  onView,
}: {
  readonly api: AdminApi;
  readonly type: AdminLegalDocumentType;
  /** Change a chaque ecriture et a chaque « Actualiser » : l'historique est relu. */
  readonly token: number;
  readonly viewing: number | null;
  readonly onView: (number: number) => void;
}): ReactElement => {
  const [pageNumber, setPageNumber] = useState(1);
  const [retries, setRetries] = useState(0);
  const list = useAdminQuery(`legal-history|${type}|${String(pageNumber)}`, () => api.listLegalVersions(type, pageNumber, PER_PAGE), {
    token: token + retries,
  });
  const page = list.data;
  const failure = list.failure;
  const pages = page === null ? 1 : Math.max(1, Math.ceil(page.total / page.perPage));

  return (
    <Card title="Historique" subtitle="Les versions publiées ne se modifient plus : on en publie une autre.">
      {failure?.forbidden === true ? (
        <AccessDenied title="Votre rôle ne permet pas de consulter l’historique">
          Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »).
        </AccessDenied>
      ) : null}
      {failure !== null && !failure.forbidden ? (
        <ErrorState
          message={failure.message}
          onRetry={() => {
            setRetries((current) => current + 1);
          }}
        />
      ) : null}
      {page === null && failure === null ? <LoadingState label="Chargement de l’historique…" /> : null}
      {page !== null && page.items.length === 0 ? (
        <EmptyState title="Aucune version" description="Ce document n’a encore aucune version publiée." />
      ) : null}
      {page !== null && page.items.length > 0 ? (
        <>
          <DataTable<AdminLegalVersionSummary>
            label="Historique des versions"
            columns={columns(onView, viewing)}
            rows={page.items}
            getRowId={(version) => (version.number === null ? 'draft' : String(version.number))}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="S"
              disabled={pageNumber <= 1 || list.busy}
              onClick={() => {
                setPageNumber(pageNumber - 1);
              }}
            >
              Page précédente
            </Button>
            <Typography role="status" sx={{ fontSize: 13, color: 'tokens.textMuted', textAlign: 'center' }}>
              {`${page.total.toLocaleString('fr-FR')} version${page.total > 1 ? 's' : ''} · page ${String(pageNumber)} sur ${String(pages)}`}
            </Typography>
            <Button
              size="S"
              disabled={pageNumber >= pages || list.busy}
              onClick={() => {
                setPageNumber(pageNumber + 1);
              }}
            >
              Page suivante
            </Button>
          </Box>
        </>
      ) : null}
    </Card>
  );
};
